"use client";

import { Typography } from "@health/ui";
import { cn } from "@health/ui/utils";
import Form from "@rjsf/core";
import { getTemplate, RegistryWidgetsType, RJSFSchema, UiSchema, WidgetProps } from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { CheckboxesWidget } from "./json-form/CheckboxesWidget";

type StepperJsonFormProps = {
  schema: RJSFSchema;
  uiSchema?: UiSchema;
  currentFieldIndex: number;
  totalFields: number;
  fieldAnswers: Record<string, unknown>;
  onFieldSubmit: (fieldName: string, value: unknown) => void;
  onBack: () => void;
  canGoBack: boolean;
  onCurrentDataChange: (fieldName: string, value: unknown) => void;
  hideWidget?: boolean;
};

/** Renders an <input type="number">. */
function NumberWidget(props: WidgetProps) {
  const { options, registry } = props;
  const BaseInputTemplate = getTemplate("BaseInputTemplate", registry, options);
  return <BaseInputTemplate type="number" {...props} />;
}

const customWidgets: RegistryWidgetsType = {
  CheckboxesWidget: CheckboxesWidget,
  number: NumberWidget,
  integer: NumberWidget,
};

export function StepperJsonForm({
  schema,
  uiSchema,
  currentFieldIndex,
  totalFields,
  fieldAnswers,
  onFieldSubmit,
  onBack,
  canGoBack,
  onCurrentDataChange,
  hideWidget = false,
}: StepperJsonFormProps) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const [_currentFormData, setCurrentFormData] = useState<Record<string, unknown>>({});

  const properties = schema.properties as Record<string, RJSFSchema> | undefined;
  if (!properties) {
    return null;
  }

  const fieldKeys = Object.keys(properties);
  if (currentFieldIndex >= fieldKeys.length) {
    return null;
  }

  const currentFieldKey = fieldKeys[currentFieldIndex];
  const currentFieldSchema = properties[currentFieldKey];
  const currentFieldUiSchema = uiSchema?.[currentFieldKey];

  // Create single-field schema for RJSF
  const singleFieldSchema: RJSFSchema = {
    type: "object",
    properties: {
      [currentFieldKey]: currentFieldSchema,
    },
    required: schema.required?.includes(currentFieldKey) ? [currentFieldKey] : [],
  };

  // Preserve existing uiSchema options
  const singleFieldUiSchema: UiSchema = currentFieldUiSchema
    ? { [currentFieldKey]: currentFieldUiSchema }
    : {};

  // Prepopulate with existing answer if available
  const formData =
    fieldAnswers[currentFieldKey] !== undefined
      ? { [currentFieldKey]: fieldAnswers[currentFieldKey] }
      : {};

  const handleChange = (data: { formData?: Record<string, unknown> }) => {
    if (!data.formData) return;

    const value = data.formData[currentFieldKey];
    setCurrentFormData(data.formData);

    // Notify parent of current data (for chat input submission)
    onCurrentDataChange(currentFieldKey, value);

    // Auto-submit for radio fields
    if (isRadio && value !== undefined && value !== null && value !== "") {
      setValidationError(null);
      onFieldSubmit(currentFieldKey, value);
    }
  };

  const handleBackClick = () => {
    setValidationError(null);
    onBack();
  };

  // Determine if current field is a radio button
  const isRadio =
    Array.isArray(currentFieldSchema.enum) &&
    currentFieldSchema.enum.length > 0 &&
    (currentFieldUiSchema as { "ui:widget"?: string })?.["ui:widget"] === "radio";

  // Only show navigation header for multi-field forms
  const showNavigation = totalFields > 1;

  // If widget is hidden (text/number input), show question label only
  if (hideWidget) {
    const currentFieldTitle = (currentFieldSchema.title as string) || currentFieldKey;
    const currentFieldDescription = currentFieldSchema.description as string | undefined;

    return (
      <div>
        {/* Pagination header - only for multi-field forms */}
        {showNavigation && (
          <div className="flex items-center justify-between mb-2 p-2">
            <button
              type="button"
              onClick={handleBackClick}
              disabled={!canGoBack}
              className={cn(
                "h-12 w-13 flex items-center justify-center rounded-[56px] border border-secondary/20 bg-secondary/10 transition-opacity",
                canGoBack ? "opacity-100 cursor-pointer" : "opacity-70 cursor-not-allowed",
              )}
              aria-label="Go back"
            >
              <ChevronLeft className="size-6" />
            </button>

            <div className="text-size-chips font-semibold text-center tracking-[-0.5px] leading-snug">
              <span className="font-semibold">{schema.title || "Form"}</span>
              <span className="font-normal"> — </span>
              <span className="text-teal-highlight">{currentFieldIndex + 1}</span>
              <span className="font-normal"> of {totalFields}</span>
            </div>

            <div className="h-12 w-[52px] flex items-center justify-center rounded-[56px] border border-secondary/20 bg-secondary/10 cursor-pointer">
              <ChevronRight className="size-6" />
            </div>
          </div>
        )}

        {/* Question label for text/number inputs */}
        <div className="bg-widget-bg rounded-2xl p-4">
          <h3 className="mb-1 text-size-question text-text-primary font-medium">
            {currentFieldTitle}
          </h3>
          {currentFieldDescription && <Typography.Hint>{currentFieldDescription}</Typography.Hint>}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Pagination header - only for multi-field forms */}
      {showNavigation && (
        <div className="flex items-center justify-between mb-2 p-2">
          <button
            type="button"
            onClick={handleBackClick}
            disabled={!canGoBack}
            className={cn(
              "h-12 w-13 flex items-center justify-center rounded-[56px] border border-secondary/20 bg-secondary/10 transition-opacity",
              canGoBack ? "opacity-100 cursor-pointer" : "opacity-70 cursor-not-allowed",
            )}
            aria-label="Go back"
          >
            <ChevronLeft className="size-6" />
          </button>

          <div className="text-size-chips font-semibold text-center tracking-[-0.5px] leading-snug">
            <span className="font-semibold">{schema.title || "Form"}</span>
            <span className="font-normal"> — </span>
            <span className="text-teal-highlight">{currentFieldIndex + 1}</span>
            <span className="font-normal"> of {totalFields}</span>
          </div>

          <div className="h-12 w-13 flex items-center justify-center rounded-[56px] border border-secondary/20 bg-secondary/10 cursor-pointer">
            <ChevronRight className="size-6" />
          </div>
        </div>
      )}

      {/* Form - no submit button, submission via chat input */}
      <div className={cn("bg-widget-bg rounded-4xl p-6", "radio-chips")}>
        <Form
          schema={singleFieldSchema}
          uiSchema={singleFieldUiSchema}
          validator={validator}
          formData={formData}
          onChange={handleChange}
          liveValidate={false}
          showErrorList={false}
          widgets={customWidgets}
        >
          {/* don't render submit-button since chat-input's button is used for submission */}
          <button type="button" className="hidden" />
        </Form>

        {validationError && <div className="mt-2 text-sm text-red-600">{validationError}</div>}
      </div>
    </div>
  );
}
